import type { VerifiableDID } from 'iso-did/types'
import type { SignatureType } from 'iso-signatures/types'
import type { Resolver } from 'iso-signatures/verifiers/resolver.js'
import type { Resolver as DidResolver } from 'iso-did'
import type { CID } from 'multiformats/cid'
import * as dagCbor from '@ipld/dag-cbor'
import { DID } from 'iso-did'
import { sha256 } from 'multiformats/hashes/sha2'
import { CID as CIDClass } from 'multiformats/cid'
import { z } from 'zod/v4'
import * as Envelope from './envelope.js'
import * as varsig from './index.js'
import type {
    PayloadSpec,
    DecodedEnvelope,
    CborValue,
    Statement,
    SignaturePayload
} from './types.js'

/**
 * Create the signature payload for a given envelope
 */
export function signaturePayload (envelope:DecodedEnvelope<PayloadSpec>) {
    const payloadTag = `ucan/${envelope.spec}@${envelope.version}` as const
    const payload = {
        h: varsig.encode({
            enc: envelope.enc,
            alg: envelope.alg,
        }),
        [payloadTag]: envelope.payload,
    } as SignaturePayload
    return payload
}

export function nowInSeconds ():number {
    return Math.floor(Date.now() / 1000)
}

export async function cid (envelope:DecodedEnvelope<PayloadSpec>):Promise<CID> {
    const bytes = Envelope.encode({
        signature: envelope.signature,
        signaturePayload: signaturePayload(envelope),
    })
    const hash = await sha256.digest(bytes)

    return CIDClass.create(1, dagCbor.code, hash)
}

/**
 * Check if a DID and signature type are compatible
 */
export function isSigAndDidCompatible (did:VerifiableDID, sigType:SignatureType):boolean {
    if (did.verifiableDid.type === 'Ed25519' && sigType === 'Ed25519') {
        return true
    }

    if (did.verifiableDid.type === 'secp256k1' && sigType === 'ES256K') {
        return true
    }

    if (did.verifiableDid.type === 'P-256' && sigType === 'ES256') {
        return true
    }

    if (did.verifiableDid.type === 'P-384' && sigType === 'ES384') {
        return true
    }

    if (did.verifiableDid.type === 'P-521' && sigType === 'ES512') {
        return true
    }

    if (did.verifiableDid.type === 'RSA' && sigType === 'RS256') {
        return true
    }
    if (did.verifiableDid.type === 'secp256k1' && sigType === 'EIP191') {
        return true
    }

    return false
}

/**
 * Validate the expiration of a UCAN
 */
export function assertExpiration (exp:number | null):void {
    const now = nowInSeconds()
    if (exp !== null && !Number.isSafeInteger(exp)) {
        throw new TypeError(
      `UCAN expiration must be null or a safe integer. Received: ${exp}`
        )
    }

    if (exp !== null && exp < now) {
        throw new TypeError(
      `UCAN expiration must be in the future. Received: ${exp} but current time is ${now}`
        )
    }
}

/**
 * Validate the not before time of a UCAN
 */
export function assertNotBefore (nbf?:number):void {
    if (nbf && nbf > nowInSeconds()) {
        throw new Error('UCAN not valid yet')
    }
}

/**
 * Validate the issuer and signature of a UCAN
 */
export async function validateIssuerAndSignature (
    envelope:DecodedEnvelope<PayloadSpec>,
    didResolver?:DidResolver
):Promise<VerifiableDID> {
    const issuer = await DID.fromString(envelope.payload.iss, didResolver)

    if (!isSigAndDidCompatible(issuer, envelope.alg)) {
        throw new Error(
      `UCAN issuer type mismatch: DID ${issuer.verifiableDid.type} and Signature ${envelope.alg} are not compatible`
        )
    }

    return issuer
}

/**
 * Verify the signature of a UCAN and that issuer is compatible with the signature
 */
export async function verifySignature (
    envelope:DecodedEnvelope<PayloadSpec>,
    signatureVerifierResolver:Resolver,
    didResolver?:DidResolver
):Promise<boolean> {
    const issuer = await validateIssuerAndSignature(envelope, didResolver)
    const isVerified = await signatureVerifierResolver.verify({
        signature: envelope.signature,
        message: dagCbor.encode(signaturePayload(envelope)),
        did: issuer,
        type: envelope.alg,
    })

    if (!isVerified) {
        throw new Error('UCAN signature verification failed')
    }

    return true
}

/**
 * Asserts that a UCAN command string is syntactically valid.
 * If the command is invalid, it throws a descriptive error.
 *
 * Rules:
 * - Commands MUST begin with a slash (/).
 * - Commands MUST be lowercase.
 * - A trailing slash MUST NOT be present, unless the command is exactly "/".
 * - Segments MUST be separated by a slash (e.g., no empty segments like "//").
 *
 * @throws Error if the command is syntactically invalid.
 */
export function assertIsValidCommand (command:string):void {
    // Rule: Must be a string
    if (typeof command !== 'string') {
        throw new TypeError(
      `Invalid command: Input must be a string, but received type ${typeof command}`
        )
    }

    // Rule: Must begin with a slash
    if (!command.startsWith('/')) {
        throw new TypeError(
      `Invalid command: Must begin with a slash (/). Received: "${command}"`
        )
    }

    // Rule: No trailing slash, unless the command is exactly "/"
    if (command.length > 1 && command.endsWith('/')) {
        throw new TypeError(
      `Invalid command: Must not have a trailing slash. Received: "${command}"`
        )
    }

    // Rule: Must be lowercase
    if (command !== command.toLowerCase()) {
        throw new TypeError(
      `Invalid command: Must be lowercase. Received: "${command}"`
        )
    }

    // Rule: No empty segments
    if (command.includes('//')) {
        throw new TypeError(
      `Invalid command: Must not contain empty segments (e.g., "//"). Received: "${command}"`
        )
    }

    // If no error was thrown, the command is valid.
}

/**
 * Assert that the input is a Uint8Array.
 *
 * @throws Error if nonce is not a Uint8Array.
 *
 * @example
 * ```ts twoslash
 * import { assertNonce } from 'iso-ucan/utils'
 * assertNonce(new Uint8Array([1,2,3])) // ok
 * assertNonce('foo') // throws
 * ```
 */
export function assertNonce (nonce:unknown):void {
    if (!(nonce instanceof Uint8Array)) {
        throw new TypeError(
      `Invalid nonce: Expected Uint8Array, got ${Object.prototype.toString.call(nonce)}`
        )
    }
}

/**
 * Assert that args is a CBOR serializable object.
 */
export function assertArgs (args:unknown):void {
    const parsed = cborObject.safeParse(args)
    if (parsed.error) {
        const pretty = z.prettifyError(parsed.error)

        throw new TypeError(`Invalid args: ${pretty}`, { cause: parsed.error })
    }
}

/**
 * Assert that the input is a Record<PropertyKey, unknown>.
 *
 * @throws Error if meta is not a Record<PropertyKey, unknown>.
 */
export function assertMeta (meta:unknown):void {
    if (meta) {
        const parsed = cborObject.safeParse(meta)
        if (parsed.error) {
            const pretty = z.prettifyError(parsed.error)

            throw new TypeError(`Invalid meta: ${pretty}`, { cause: parsed.error })
        }
    }
}

/**
 * Assert that the input is not revoked.
 */
export async function assertNotRevoked (cid:CID, isRevokedFn?:(cid:CID) => Promise<boolean>):Promise<void> {
    isRevokedFn = isRevokedFn ?? (async () => false)
    const isRevoked = await isRevokedFn(cid)
    if (isRevoked) {
        throw new Error('UCAN revoked')
    }
}

/**
 * Expiration or TTL (default 300 seconds)
 */
export function expOrTtl ({ exp, ttl }:{ exp?:number | null; ttl?:number }):number | null {
    if (exp === null) {
        return exp
    }
    const currentTimeInSeconds = Math.floor(Date.now() / 1000)
    const expiration = exp ?? currentTimeInSeconds + (ttl ?? 300)

    return expiration
}

export const cborValue: z.ZodType<CborValue> = z.lazy(() => {
    return z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.null(),
        z.bigint(),
        z.instanceof(Uint8Array),
        z.array(cborValue),
        z.record(z.string(), cborValue),
    ])
})

export const cborObject = z.record(z.string(), cborValue)

export const selector = z.union([
    z.literal('.'),
    z.string().regex(/^\.([a-zA-Z0-9_]+)$/),
])

export type Selector = z.infer<typeof selector>

/**
 * Statement
 */
export const statement:z.ZodType<Statement<unknown>> = z.lazy(() => {
    return z.union([
        z.tuple([
            z.union([z.literal('=='), z.literal('!=')]),
            z.literal('.'),
            z.unknown(),
        ]), // Equality
        z.tuple([
            z.union([
                z.literal('<'),
                z.literal('<='),
                z.literal('>'),
                z.literal('>='),
            ]),
            z.literal('.'),
            z.number(),
        ]), // Inequality
        z.tuple([z.literal('like'), z.literal('.'), z.string()]), // Like
        z.tuple([z.literal('not'), statement]),
        z.tuple([z.literal('and'), z.array(statement)]),
        z.tuple([z.literal('or'), z.array(statement)]),
        z.tuple([z.literal('all'), z.literal('.'), statement]),
        z.tuple([z.literal('any'), z.literal('.'), statement]),
    ]) as z.ZodType<Statement<unknown>>
})

export const policySchema = z.array(statement)

export function assertPolicy (policy:unknown):void {
    const parsed = policySchema.safeParse(policy)
    if (parsed.error) {
        const pretty = z.prettifyError(parsed.error)

        throw new TypeError(`Invalid policy: ${pretty}`, { cause: parsed.error })
    }
}

/**
 * A type guard for Record<PropertyKey, unknown>.
 *
 * @returns Whether the specified value has a runtime type of `object` and is
 * neither `null` nor an `Array`.
 */
export function isObject (value:unknown):value is Record<PropertyKey, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}
